import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Send, Trash2, Loader2, CheckCircle2, Hourglass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

export const ReviewsPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [existing, setExisting] = useState(null);

  const fetchExisting = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reviews/me`, { withCredentials: true });
      if (data && data.id) {
        setExisting(data);
        setRating(data.rating || 0);
        setComment(data.comment || '');
      }
    } catch {
      // Pas grave
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExisting(); }, []);

  const submit = async () => {
    if (rating === 0) {
      toast.error('Sélectionne entre 1 et 5 étoiles');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { rating, comment }, { withCredentials: true });
      toast.success(existing ? 'Avis mis à jour — sera revérifié par l\'équipe avant publication' : 'Merci ! Ton avis sera publié après validation');
      await fetchExisting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer ton avis ?')) return;
    try {
      await axios.delete(`${API}/reviews/me`, { withCredentials: true });
      toast.success('Avis supprimé');
      setExisting(null);
      setRating(0);
      setComment('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  const ratingDisplay = hover || rating;
  const status = existing
    ? (existing.public ? { icon: CheckCircle2, label: 'Avis publié', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' }
                       : { icon: Hourglass, label: 'En attente de validation', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' })
    : null;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6" data-testid="reviews-page">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight font-['Outfit']">Laisser un avis</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Ton retour aide à faire évoluer Home MDB 2A. Les avis validés peuvent être affichés
          publiquement sur la landing page (avec ton prénom et l'initiale de ton nom uniquement).
        </p>
      </div>

      {status && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${status.color}`} data-testid="review-status">
          <status.icon className="w-4 h-4" />
          <span className="font-semibold">{status.label}</span>
          {existing.featured && <span className="ml-auto text-xs">⭐ Mis en avant</span>}
        </div>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">Note globale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-center gap-2 py-3" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`review-star-${n}`}
                aria-label={`${n} étoiles`}
              >
                <Star
                  className={`w-10 h-10 sm:w-12 sm:h-12 transition-colors ${
                    n <= ratingDisplay ? 'fill-amber-400 text-amber-400' : 'text-zinc-700'
                  }`}
                />
              </button>
            ))}
          </div>
          <div className="text-center text-zinc-500 text-sm">
            {ratingDisplay === 0 ? 'Choisis une note de 1 à 5'
              : ratingDisplay === 1 ? 'Très insatisfait'
              : ratingDisplay === 2 ? 'Insatisfait'
              : ratingDisplay === 3 ? 'Correct'
              : ratingDisplay === 4 ? 'Satisfait — recommande'
              : 'Excellent — fortement recommandé'}
          </div>

          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
              Ton commentaire (optionnel)
            </label>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Ex : J'utilise l'app sur mes opérations depuis 2 mois, j'ai gagné 3h par semaine sur les études..."
              rows={5}
              maxLength={2000}
              className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-amber-500/40"
              data-testid="review-comment"
            />
            <div className="text-right text-xs text-zinc-600 mt-1">{comment.length} / 2000</div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            {existing && (
              <Button
                variant="outline"
                onClick={remove}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                data-testid="review-delete"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </Button>
            )}
            <Button
              onClick={submit}
              disabled={submitting || rating === 0}
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
              data-testid="review-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {existing ? 'Mettre à jour mon avis' : 'Envoyer mon avis'}
            </Button>
          </div>

          <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">
            ⓘ Confidentialité : seul ton prénom + l'initiale de ton nom seront affichés publiquement.
            Ton email reste privé. Tu peux modifier ou supprimer ton avis à tout moment.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewsPage;
